import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import styled from "@emotion/styled/macro";
import { useParams } from "react-router-dom";
import {
  Button,
  Modal,
  tokens,
  LoadingIndicator,
  LoadingOverlay,
  Snackbar,
  Typography,
} from "@sunrun/experience-ui-components";
import { isSunrunManaged } from "@sunrun/sales-experience-shared";
import { useDesignRequest } from "../../hooks/useDesignRequest";
import { useSiteModelInfo } from "../../hooks/useSiteModelInfo";
import { ListOffer } from "../../../amplify/backend/function/offerexpstoreFrontApi/ts/public/offerTypes";
import refreshIcon from "../assets/images/refresh.svg";
import {
  FetchProductAvailabilityContext,
  NewOfferContext,
} from "providers/NewOfferProvider";
import {
  HeaderButton,
  HeaderContentLayout,
} from "components/templates/HeaderContentLayout";
import { useAppSelector } from "store";
import {
  customerListOffers,
  listOffers,
  syncSalesforce,
} from "services/storeFront";
import {
  useNavigateToConfirm,
  useNavigateToBuild,
  reloadMFE,
} from "utils/useNavigateHelper";
import { ShareProspectForm } from "components/organisms/ShareProspectForm";
import { ErrorComponent } from "components/atoms/ErrorComponent";
import { sendTrackingEvent } from "services/fullstory";
import { DesignRequest } from "providers/Types";
import { useContactsData } from "utils/swrHooks";
import { getOfferContact } from "utils/contacts";
import { OfferList } from "components/organisms/OfferList";
import { useUserProfile } from "cap/hooks/useCapSwr";
import { excludeBuilderOffers } from "utils/builderOffer";
import { useUsageData } from "utils/swrHooks";
import { isNewHomesSalesDivision } from "utils/newHomes";
import {
  isSiteModelComplete,
  getSiteModelMinimumValidDate,
} from "utils/siteModelUtils";

type CreateOfferSplashPageProps = {
  bypassSiteModel: boolean;
  prospectId?: string;
  setBypassSiteModel: (value: boolean) => void;
  setError: React.Dispatch<React.SetStateAction<Error | undefined>>;
  syncSalesforceClick: () => void;
  showProceedWithoutSiteModelButton: boolean;
  isLoadingOfferContext: boolean;
  doNavigateToBuild: (source: string) => void;
  designRequest?: DesignRequest;
  siteModelImageUrl?: string | null;
  siteModelHasRoofPlanes?: boolean;
  designRequests: Array<
    Pick<
      DesignRequest,
      "createdDate" | "notesToDesigner" | "priority" | "queue" | "status"
    >
  >;
  isDesignRequestRequired: boolean;
  isLoading: boolean;
  error: Error | undefined;
  isNewHomes?: boolean;
};

const CreateOfferSplashPage = ({
  bypassSiteModel,
  showProceedWithoutSiteModelButton,
  isLoadingOfferContext,
  setError,
  setBypassSiteModel,
  syncSalesforceClick,
  doNavigateToBuild,
  designRequest,
  siteModelImageUrl,
  siteModelHasRoofPlanes = false,
  designRequests,
  isDesignRequestRequired,
  isLoading,
  error,
  isNewHomes = false,
}: CreateOfferSplashPageProps) => {
  const navigateToBuild = useNavigateToBuild();
  const navigateToConfirm = useNavigateToConfirm();
  const isSiteModelReady: boolean = useMemo(() => {
    return isSiteModelComplete(
      isDesignRequestRequired,
      designRequest,
      siteModelHasRoofPlanes
    );
  }, [designRequest, isDesignRequestRequired, siteModelHasRoofPlanes]);

  const canBypassDesignRequest: boolean = useMemo(() => {
    return Boolean(
      designRequest?.queue === "Redesign" &&
        designRequest?.status === "Failure" &&
        designRequests?.find(
          (dr) => dr.queue === "Design" && dr.status === "Complete"
        ) &&
        siteModelHasRoofPlanes
    );
  }, [designRequest, designRequests, siteModelHasRoofPlanes]);

  if (isLoading) {
    return <LoadingIndicator fullScreen color="black" />;
  }

  if (error) {
    setError(error);
  }

  const siteModelMissing =
    isDesignRequestRequired &&
    !siteModelImageUrl &&
    designRequest?.status === "Complete";
  const siteModelExists = isDesignRequestRequired
    ? Boolean(siteModelImageUrl)
    : siteModelHasRoofPlanes;

  let title = "Your Site Model is Being Created";
  if (siteModelMissing) {
    title = "No Site Model Available";
  } else if (designRequest?.status === "Failure") {
    title = "Site Model Update Failed";
  } else if (siteModelExists) {
    title = "Your Site Model is Getting Updated";
  }

  let designRequestInfo = (
    <SplashTitle>
      Your {designRequest?.queue ? `${designRequest.queue}` : "Design"}
      &nbsp;is {designRequest?.status ?? "Not Started"}
      {siteModelMissing && ", but no Site Model exists!"}
    </SplashTitle>
  );
  if (designRequest?.status === "Failure") {
    designRequestInfo = (
      <SplashTitle>
        Please return to Confirm Info to see the failure reason.
      </SplashTitle>
    );
  } else if (siteModelMissing) {
    designRequestInfo = (
      <SplashTitle>
        Please return to Confirm Info and request an updated site model.
      </SplashTitle>
    );
  }

  const showSiteModelPrompt: boolean = !isSiteModelReady && !bypassSiteModel;

  return (
    <CreateOfferSplashPageContainer>
      <SyncSalesforceButtonContainer>
        <Button
          onClick={syncSalesforceClick}
          size="sm"
          color="tertiary"
          iconLeft={<img src={refreshIcon} alt="refresh" />}
        >
          Refresh Offer List
        </Button>
      </SyncSalesforceButtonContainer>
      <div data-testid="splash-page-container">
        {showSiteModelPrompt && (
          <>
            <SplashSubTitle data-testid="oe-site-model-not-ready">
              {title}
            </SplashSubTitle>
            {designRequestInfo}
            {!!designRequest?.queuePosition &&
              designRequest?.queuePosition > 0 && (
                <SplashTitle>
                  {designRequest.queuePosition} in front of you in the queue
                </SplashTitle>
              )}
            <SplashActions>
              {isLoadingOfferContext ? (
                <LoadingIndicator color="white" showLoadingMessage={false} />
              ) : (
                <>
                  {showProceedWithoutSiteModelButton && !isNewHomes && (
                    <Button
                      onClick={() =>
                        navigateToBuild(undefined, undefined, {
                          skipToPricing: false,
                        })
                      }
                      color="secondary"
                    >
                      Proceed without site model
                    </Button>
                  )}
                </>
              )}

              <CustomButton onClick={() => navigateToConfirm()}>
                Confirm Info
              </CustomButton>
              {canBypassDesignRequest && (
                <CustomButton onClick={() => setBypassSiteModel(true)}>
                  Proceed with Previous Site Model
                </CustomButton>
              )}
            </SplashActions>
          </>
        )}

        {!showSiteModelPrompt && !isNewHomes && (
          <>
            <SplashSubTitle>
              Let's get started on your first offer
            </SplashSubTitle>
            <StyledButton>
              <Button
                onClick={() => doNavigateToBuild("Start")}
                size="md"
                color="primary"
                style={{ width: 200 }}
                data-testid={"create-offer-btn"}
              >
                Create an Offer
              </Button>
            </StyledButton>
          </>
        )}
      </div>
    </CreateOfferSplashPageContainer>
  );
};

const hasBundleWithSiteModelNotRequired = (
  productAvailability:
    | { bundles?: Array<{ siteModelRequired?: boolean }> }
    | undefined
): boolean => {
  if (!productAvailability?.bundles) return false;
  return productAvailability.bundles.some(
    (bundle) => bundle.siteModelRequired === false
  );
};

const OfferListPage = () => {
  const { prospectId } = useParams();
  const [isLoadingOfferContext, setIsLoadingOfferContext] = useState(false);
  const newOffer = useContext(NewOfferContext);
  const refreshProductAvailability = useContext(
    FetchProductAvailabilityContext
  );
  useEffect(() => {
    setIsLoadingOfferContext(true);
    if (newOffer) {
      setIsLoadingOfferContext(false);
    }
  }, [newOffer]);

  const cognitoToken = useAppSelector(
    (state) => state.auth.cognitoToken?.token
  );

  const proposalToken = useAppSelector((state) => state.auth.proposalToken);
  const identity = useAppSelector((state) => state.auth.identity);

  const [offers, setOffers] = useState<ListOffer[]>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showModal, setShowModal] = useState<string | undefined>(undefined);
  const [showShareModal, setShowShareModal] = useState<boolean>(false);
  const [showRedesignRequestModal, setShowRedesignRequestModal] =
    useState<boolean>(false);
  const [showOpenDesignRequestModal, setShowOpenDesignRequestModal] =
    useState<boolean>(false);
  const [snackBarState, setSnackBarState] = useState<
    | {
        type: "success" | "error" | "warning" | "info";
        message: string;
        onHide?: () => void;
      }
    | undefined
  >(undefined);
  const [error, setError] = useState<Error>();
  const [bypassSiteModel, setBypassSiteModel] = useState(false);

  const navigateToConfirm = useNavigateToConfirm();
  const navigateToBuild = useNavigateToBuild();

  const hybridToken = useAppSelector((state) => state?.auth?.hybridToken);
  const { contacts } = useContactsData(prospectId!, hybridToken);
  const { prospect } = useUsageData(prospectId!);

  const { userProfile } = useUserProfile(hybridToken);
  const isSRM = userProfile && isSunrunManaged(userProfile);
  const isNewHomes = isNewHomesSalesDivision(prospect?.salesDivision);

  const offerContact = useMemo(
    () => getOfferContact(contacts, offers),
    [contacts, offers]
  );

  const isCustomer = !!proposalToken;
  const visibleOffers = useMemo(() => {
    if (!offers) {
      return undefined;
    }

    return isCustomer ? excludeBuilderOffers(offers) : offers;
  }, [offers, isCustomer]);

  const [
    showProceedWithoutSiteModelButton,
    setShowProceedWithoutSiteModelButton,
  ] = useState(false);

  useEffect(() => {
    if (newOffer && "productAvailability" in newOffer) {
      const shouldShow = hasBundleWithSiteModelNotRequired(
        newOffer.productAvailability
      );
      setShowProceedWithoutSiteModelButton(shouldShow);
    }
  }, [newOffer]);

  const {
    data: {
      designRequest,
      designRequests,
      isDesignRequestRequired = true,
    } = {},
    error: redesignRequestError,
    isLoading: isDesignRequestLoading,
  } = useDesignRequest(prospectId, { refreshInterval: 1000 * 60 });
  const siteModelMinimumValidDate =
    getSiteModelMinimumValidDate(designRequests);
  const {
    data: { siteModelImageUrl, siteModelHasRoofPlanes = false } = {},
    isLoading: isSiteModelInfoLoading,
  } = useSiteModelInfo(
    prospectId,
    {
      refreshInterval: 1000 * 60,
    },
    siteModelMinimumValidDate
  );
  const isRedesignRequestLoading =
    isDesignRequestLoading || isSiteModelInfoLoading;

  const onReloadData = useCallback(() => {
    if (((!cognitoToken || !identity) && !proposalToken) || !prospectId) return;
    setIsLoading(true);
    let request;
    if (cognitoToken && identity) {
      request = listOffers(cognitoToken, prospectId, identity);
    } else if (proposalToken) {
      request = customerListOffers(proposalToken);
    } else {
      setIsLoading(false);
      setError(new Error("Invalid Auth Token type!"));
      return;
    }

    request
      .then((data) => {
        if (data && data.length > 0) {
          setOffers(data);
        } else {
          setOffers([]);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [cognitoToken, proposalToken, prospectId, identity]);

  useEffect(() => {
    if ((!cognitoToken && !proposalToken) || !prospectId) return;
    onReloadData();
  }, [cognitoToken, proposalToken, prospectId, onReloadData]);
  const onShareClick = useCallback(() => {
    setShowShareModal(true);
  }, []);
  const closeShareModal = useCallback(() => {
    setShowShareModal(false);
  }, []);

  const onSendOffersStart = useCallback(() => {
    closeShareModal();
    setShowModal("Sending offers...");
  }, [closeShareModal]);

  const onSendOffers = useCallback(
    (type: "success" | "error" | "warning" | "info", message: string) => {
      setShowModal(undefined);
      // Succuss or failure, reload the page to show the current state
      // Set a timeout to reload to show the snackbar
      setSnackBarState({
        type,
        message,
        onHide: () => {
          reloadMFE();
        },
      });
    },
    [setSnackBarState, setShowModal]
  );
  const syncSalesforceClick = useCallback(() => {
    if (!cognitoToken || !prospectId) return;
    setIsLoading(true);
    syncSalesforce(cognitoToken, prospectId)
      .catch(setError)
      .finally(() => {
        // Success or failure, reload the page to show the current state
        reloadMFE();
      });
  }, [cognitoToken, prospectId]);

  const oneOffer = offers?.length === 1;

  const hasOffers = useMemo(() => {
    return offers && offers.length > 0;
  }, [offers]);
  const showSplash: boolean = !bypassSiteModel && !hasOffers;

  const doNavigateToBuild = useCallback(
    (source: string = "Create New Offer") => {
      sendTrackingEvent("New Offer Start", { source });
      refreshProductAvailability?.();
      navigateToBuild(undefined, undefined, { skipToPricing: false });
    },
    [navigateToBuild, refreshProductAvailability]
  );

  const checkIfRedesignRequestIsInProgress = useCallback(() => {
    const hasRedesignRequest =
      designRequest &&
      (["Redesign"] as Array<DesignRequest["queue"]>).includes(
        designRequest.queue
      ) &&
      (designRequest.status === "Open" ||
        designRequest.status === "In Progress");
    const hasOpenDesignRequest =
      designRequest &&
      (["Design"] as Array<DesignRequest["queue"]>).includes(
        designRequest.queue
      ) &&
      (designRequest.status === "Open" ||
        designRequest.status === "In Progress");
    if (hasRedesignRequest) {
      setShowRedesignRequestModal(true);
      return true;
    } else if (hasOpenDesignRequest) {
      setShowOpenDesignRequestModal(true);
      return true;
    } else {
      return false;
    }
  }, [designRequest]);

  const onAddNewOffer = useCallback(() => {
    const showDesignRequestModal = checkIfRedesignRequestIsInProgress();
    if (showDesignRequestModal) {
      return;
    } else {
      doNavigateToBuild("Add New Offer");
    }
  }, [checkIfRedesignRequestIsInProgress, doNavigateToBuild]);

  const buttons = useMemo(() => {
    if (!hasOffers || !cognitoToken) {
      return [];
    }

    const headerButtons: HeaderButton[] = [
      {
        type: "icon",
        icon: "refresh",
        text: "Refresh Offer List",
        altText: "refresh",
        borderless: true,
        onClick: syncSalesforceClick,
      } as HeaderButton,
      {
        type: "icon",
        icon: "share",
        altText: "Share Offers",
        onClick: onShareClick,
      } as HeaderButton,
      {
        type: "secondary",
        text: "Back to Info & Tasks",
        onClick: navigateToConfirm,
      } as HeaderButton,
    ];

    if (!isNewHomes) {
      headerButtons.push({
        type: "primaryWithIcon",
        icon: "plus",
        text: "Add new offer",
        altText: "Add new offer",
        onClick: onAddNewOffer,
        dataTestId: "add-new-offer-btn",
      } as HeaderButton);
    }

    return headerButtons;
  }, [
    hasOffers,
    cognitoToken,
    isNewHomes,
    onShareClick,
    navigateToConfirm,
    syncSalesforceClick,
    onAddNewOffer,
  ]);

  if (error || redesignRequestError) {
    return (
      <HeaderContentLayout
        content={
          <ErrorComponent
            context="OfferListPage"
            action="Get Offer List"
            error={error}
          />
        }
        title="There was a Problem."
        description="Something went wrong. Please try refreshing the page."
      />
    );
  }

  if (!prospectId) {
    return (
      <HeaderContentLayout
        content={
          <ErrorComponent
            context="OfferListPage"
            action="Get Prospect"
            error={new Error(`${prospectId} not found`)}
          />
        }
        title="There was a Problem."
        description="Something went wrong. Please try refreshing the page."
      />
    );
  }
  if (isLoading || !offers || isRedesignRequestLoading) {
    return <LoadingIndicator fullScreen color="black" />;
  }
  return (
    <>
      {showRedesignRequestModal && (
        <Modal
          testId="redesign-request-modal"
          type="warning"
          title="You have a redesign request in progress"
          onClose={() => setShowRedesignRequestModal(false)}
          primaryButton={{
            text: "Proceed w/old site model",
            onClick: () => {
              setShowRedesignRequestModal(false);
              doNavigateToBuild();
            },
          }}
          secondaryButton={{
            text: "Cancel",
            onClick: () => setShowRedesignRequestModal(false),
          }}
        >
          <Typography size={"large"}>
            The site modeling team is still working on your redesign request. If
            you proceed with a solar bundle it will be on the{" "}
            <b>old site model</b>.{" "}
          </Typography>
        </Modal>
      )}
      {showOpenDesignRequestModal && (
        <Modal
          testId="design-request-modal"
          type="warning"
          title="You have a design request in progress"
          onClose={() => setShowOpenDesignRequestModal(false)}
          primaryButton={{
            text: "Proceed",
            onClick: () => {
              setShowOpenDesignRequestModal(false);
              doNavigateToBuild();
            },
          }}
          secondaryButton={{
            text: "Cancel",
            onClick: () => setShowOpenDesignRequestModal(false),
          }}
        >
          <Typography size={"large"}>
            The site modeling team is still working on your initial design
            request. If you proceed, you may only be able to select non-solar
            bundles.{" "}
          </Typography>
        </Modal>
      )}
      {showSplash ? (
        <CreateOfferSplashPage
          prospectId={prospectId}
          setError={setError}
          bypassSiteModel={bypassSiteModel}
          setBypassSiteModel={setBypassSiteModel}
          syncSalesforceClick={syncSalesforceClick}
          showProceedWithoutSiteModelButton={showProceedWithoutSiteModelButton}
          isLoadingOfferContext={isLoadingOfferContext}
          designRequest={designRequest ?? undefined}
          siteModelImageUrl={siteModelImageUrl}
          siteModelHasRoofPlanes={siteModelHasRoofPlanes}
          designRequests={designRequests || []}
          doNavigateToBuild={doNavigateToBuild}
          isDesignRequestRequired={isDesignRequestRequired}
          isLoading={isRedesignRequestLoading}
          error={redesignRequestError}
          isNewHomes={isNewHomes}
        />
      ) : (
        <HeaderContentLayout
          title="Offer Manager"
          buttons={buttons}
          content={
            <OfferList
              onAddNewOffer={onAddNewOffer}
              isSRM={isSRM}
              offers={visibleOffers ?? []}
              hideAddNewOffer={isNewHomes}
              onLoading={setShowModal}
              setError={setError}
            />
          }
        />
      )}

      {showShareModal && offers && (
        <StyledShareModal
          onClose={closeShareModal}
          customModalWidth={900}
          testId="offer-list-page-share-modal"
          title={oneOffer ? "Share Offer" : "Share Offers"}
          hideIcon
        >
          <ShareProspectForm
            isPartner={userProfile?.isPartner}
            prospectId={prospectId}
            offers={offers}
            customerName={
              offerContact?.customerFirstName +
              " " +
              offerContact?.customerLastName
            }
            customerEmail={offerContact?.customerEmail}
            customerPrimaryPhone={offerContact?.customerPrimaryPhone}
            onSubmitStart={onSendOffersStart}
            onSubmitComplete={onSendOffers}
            closeModal={closeShareModal}
          />
        </StyledShareModal>
      )}
      {showModal && (
        <>
          <LoadingOverlay
            message={showModal}
            data-testid="share-offers-loading-overlay"
          />
        </>
      )}
      {snackBarState && (
        <StyledSnackbarContainer className="snackbar-custom-container">
          <Snackbar
            onClose={() => {
              snackBarState.onHide?.();
              setSnackBarState(undefined);
            }}
            open={!!snackBarState}
            message={snackBarState.message}
            type={snackBarState.type}
          />
        </StyledSnackbarContainer>
      )}
    </>
  );
};

const StyledSnackbarContainer = styled.div`
  z-index: 10000 !important;
  position: absolute;
`;

const SplashActions = styled.div`
  display: flex;
  gap: 14px;
  justify-content: center;
`;

const CreateOfferSplashPageContainer = styled.div`
  height: 100vh;
  width: 100%;
  background-image: linear-gradient(#a5c9ff, #fff 92%);

  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
`;

const SyncSalesforceButtonContainer = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
`;

const SplashTitle = styled.div`
  font-size: 20px;
  line-height: 24px;
  color: ${tokens.TEXT_LIGHT};
  padding-bottom: 24px;
`;

const SplashSubTitle = styled.div`
  max-width: 15em;
  font-size: 44px;
  line-height: 44px;
  color: ${tokens.BRAND_HERO_BLUE};
  padding-bottom: 24px;
`;

const CustomButton = styled.button`
  font-size: 12px;
  padding: 12px 27px;
  border-radius: 28px;
  color: ${tokens.WHITE};
  border-color: ${tokens.BRAND_HERO_BLUE};
  background: ${tokens.BRAND_HERO_BLUE};
  cursor: pointer;

  &:hover {
    opacity: 0.6;
  }
`;

const StyledButton = styled.div`
  display: inline-block;
`;

const StyledShareModal = styled(Modal)`
  .modal-content-container {
    padding: 0px 40px 40px 40px;
  }
`;

export { CreateOfferSplashPage, OfferListPage };
